import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Duration } from 'luxon';
import { Subscription } from 'rxjs';
import {
  KpiReportFilter,
  extraFiltersOnly,
} from 'src/app/common/dtos/reports.dto';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { DetailedPieData } from '../../../../shared/components/reports/report-controller/series-helper';

type DisplayData = DetailedPieData & {
  sessionDuration: Duration;
  color: string;
};

@Component({
  selector: 'app-frequency-table',
  templateUrl: './frequency-table.component.html',
  styleUrls: ['./frequency-table.component.scss'],
  standalone: false,
})
export class FrequencyTableComponent implements OnChanges, OnDestroy {
  @Input() externalData: DetailedPieData[];

  @Input() filters: KpiReportFilter[];

  @Input() reportFiltersetId: number | undefined;

  @Input() colors: string[];

  hasData = false;

  primaryData: DetailedPieData[];

  displayData?: DisplayData[];

  isLoading = false;

  private _sub: Subscription | null = null;

  constructor(private apiService: APICoreService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filters']) {
      this.getPrimaryData();
    }
    if (changes['colors']) {
      // For very large sets of data
      this.colors = [
        ...changes['colors'].currentValue,
        ...changes['colors'].currentValue,
      ];
    }
    if (changes['externalData']) {
      this.clearDisplayData();
      if (this.primaryData) {
        this.mergeData();
      }
    }
  }

  clearDisplayData() {
    this.hasData = false;
    if (this.displayData) {
      delete this.displayData;
    }
  }

  getPrimaryData() {
    this.isLoading = true;
    this.clearDisplayData();
    const params = {
      report: 'durationBySessionType',
      id: 'duration-by-session-type',
      ...(this.reportFiltersetId
        ? { report_filterset_id: this.reportFiltersetId }
        : {}),
      filters: extraFiltersOnly(this.filters, this.reportFiltersetId),
    };
    this._sub?.unsubscribe();
    this._sub = this.apiService
      .getRequest('reporting/load-report', params)
      .subscribe((value) => {
        if (value.datasets.length === 1) {
          this.primaryData = (value.datasets[0].data as number[]).map(
            (dataPoint, idx) => ({
              name: value.labels[idx],
              groupId: value.labels[idx],
              value: dataPoint,
              labelId: value.label_ids[idx],
            })
          );

          if (this.externalData) {
            this.mergeData();
          }
        }
        this.isLoading = false;
      });
  }

  mergeData() {
    this.displayData = this.externalData.map(
      (dataPoint: DetailedPieData, idx: number) => ({
        ...dataPoint,
        color: this.colors[idx],
        sessionDuration: Duration.fromObject({
          minutes: this.primaryData.find(
            (primaryDataPoint) => primaryDataPoint.labelId === dataPoint.labelId
          )?.value as number,
        }).shiftTo('hours', 'minutes'),
      })
    );
    this.hasData = true;
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }
}
